/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import ResizeWrapper from '@/components/ResizeWrapper';
import { useIsMobile } from '@/utils/utils';
import Apis from '@/app/index';
import MainArea from './MainArea';
import { ABORT_VISIBILITY_CHANGE, useLeave } from '@/lib/useCommon';
import { RTCConfig, SceneConfig, updateRTCConfig, updateScene, updateSceneConfig } from '@/store/slices/room';
import styles from './index.module.less';

export default function () {
  const leaveRoom = useLeave();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  // 从URL获取参数的工具函数
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      lessonId: params.get('lesson_id') || '',
      englishToken: params.get('english_token') || ''
    };
  };

  const getScenes = async () => {
    // 直接获取并使用参数
    const { lessonId, englishToken } = getUrlParams();

    const { scenes }: {
      scenes: { rtc: RTCConfig; scene: SceneConfig }[];
    } = await Apis.Basic.getScenes({ lessonId, englishToken });


    // 保存sessionId到sessionStorage
    const sessionId = scenes[0].scene.sessionId;
    const subtitleList = (scenes[0].scene as SceneConfig & { subtitleList?: string }).subtitleList;
    if (sessionId) {
      sessionStorage.setItem('sessionId', sessionId);
    }

    if (subtitleList) {
      sessionStorage.setItem('subtitleList', subtitleList);
    }

    dispatch(updateScene(scenes[0].scene.id));
    dispatch(updateSceneConfig(
        scenes.reduce<Record<string, SceneConfig>>((prev, cur) => {
          prev[cur.scene.id] = cur.scene;
          return prev;
        }, {})
    ));
    dispatch(updateRTCConfig(
        scenes.reduce<Record<string, RTCConfig>>((prev, cur) => {
          prev[cur.scene.id] = cur.rtc;
          return prev;
        }, {})
    ));
  };

  useEffect(() => {
    getScenes();

    const isOriginalDemo = window.location.host.startsWith('localhost');
    const handler = () => {
      if (document.visibilityState === 'hidden' &&
          !sessionStorage.getItem(ABORT_VISIBILITY_CHANGE)) {
        leaveRoom();
      }
    };

    !isOriginalDemo && document.addEventListener('visibilitychange', handler);
    return () => {
      !isOriginalDemo && document.removeEventListener('visibilitychange', handler);
    };
  }, []);

  return (
      <ResizeWrapper className={styles.container}>
        <div
            className={styles.main}
            style={{ padding: isMobile ? '' : '24px' }}
        >
          <div className={`${styles.mainArea} ${isMobile ? styles.isMobile : ''}`}>
            <MainArea />
          </div>
        </div>
      </ResizeWrapper>
  );
}
